import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { of, catchError, map } from 'rxjs';

export interface SliderItem {
  slider_id: string;
  slider_type: 'normal' | 'adult';
  src: string;
  href: string;
  target: string;
  alt: string;
  book_nid: string | null;
  position: 'header' | 'footer';
  sort_order: number;
}

// Banner tĩnh — giữ lại để không phải upload từng cái qua admin.
// Khi API trả về banner từ DB, ta merge cả 2 nguồn, deduplicate theo src (ưu tiên DB).
const STATIC_BANNERS_OLD: Record<'normal' | 'adult', Partial<SliderItem>[]> = {
  normal: [
    {
      href: '/b/JKOpqDn0o',
      src: '/frontend-assets/banners/thien-tran-bat-uy.png',
      alt: 'Thiên trản bất úy - Chiếu Trúc',
      target: '_self',
    },
    {
      href: 'https://www.facebook.com/odaydutruyenViet',
      target: '_blank',
      src: '/frontend-assets/banners/odaydutruyen.jpg',
      alt: 'ở đây đu truyện',
    },
    {
      href: '/b/cZ-Edz',
      src: '/frontend-assets/banners/neu-duoc-quay-lai-de-gap-em.png',
      alt: 'Nếu được quay lại để gặp em - Cẩm Thương',
      target: '_self',
    },
    {
      href: '/b/3BUCIM',
      src: '/frontend-assets/banners/tri-nho-cua-anh.png',
      alt: 'Trí nhớ của anh hồi ức của em - Cẩm Thương',
      target: '_self',
    },
    {
      href: '/b/SjUQDVe2l',
      src: '/frontend-assets/banners/vat-va-va-vat-vuong.png',
      alt: 'Vật vã và vất vưởng - Mộc (Talia)',
      target: '_self',
    },
  ],
  adult: [
    {
      href: '/b/S7Tli9ikc',
      src: '/frontend-assets/banners/loi-thi-tham-cua-binh-minh.png',
      alt: 'Lời thì thầm của bình minh - Cẩm Thương',
      target: '_self',
    },
    {
      href: '/b/Xfqim3',
      src: '/frontend-assets/banners/trang-dan-loi.png',
      alt: 'Trăng dẫn lối - Meeanleen',
      target: '_self',
    },
    {
      href: '/b/Gmra_0',
      src: '/frontend-assets/banners/cui-minh-hon-em.png',
      alt: 'Cúi mình hôn em - Meeanleen',
      target: '_self',
    },
  ],
};

const STATIC_BANNERS = {
  normal: [],
  adult: [],
}

function toSliderItem(
  raw: Partial<SliderItem>,
  type: 'normal' | 'adult',
  idx: number,
): SliderItem {
  return {
    slider_id: raw.slider_id ?? `static-${type}-${idx}`,
    slider_type: type,
    src: raw.src ?? '',
    href: raw.href ?? '#',
    target: raw.target ?? '_self',
    alt: raw.alt ?? '',
    book_nid: raw.book_nid ?? null,
    position: raw.position ?? 'header',
    sort_order: raw.sort_order ?? idx,
  };
}

function mergeBanners(apiItems: SliderItem[], staticItems: Partial<SliderItem>[], type: 'normal' | 'adult'): SliderItem[] {
  const apiSources = new Set(apiItems.map((i) => i.src));
  const extra = staticItems
    .filter((s) => !apiSources.has(s.src ?? ''))
    .map((s, idx) => toSliderItem(s, type, idx));
  return [...apiItems, ...extra];
}

@Injectable({ providedIn: 'root' })
export class SliderApi {
  constructor(private http: HttpClient) { }

  normalBanner() {
    return this.http
      .get<{ status: string; normal: SliderItem[]; adult: SliderItem[] }>('/v1/api/sliders')
      .pipe(
        map((res) => mergeBanners(res.normal ?? [], STATIC_BANNERS.normal, 'normal')),
        catchError(() => of(STATIC_BANNERS.normal.map((s, i) => toSliderItem(s, 'normal', i)))),
      );
  }

  adultBanner() {
    return this.http
      .get<{ status: string; normal: SliderItem[]; adult: SliderItem[] }>('/v1/api/sliders')
      .pipe(
        map((res) => mergeBanners(res.adult ?? [], STATIC_BANNERS.adult, 'adult')),
        catchError(() => of(STATIC_BANNERS.adult.map((s, i) => toSliderItem(s, 'adult', i)))),
      );
  }
}